'use client'
import { memo, useEffect, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import { useModal } from '@/hooks'
import { MODALS } from '@/context'
import styled from 'styled-components'
import { mq, vw } from '@/styles'
import { useInView } from 'motion/react'

const ReactPlayer = dynamic(() => import('react-player'), { ssr: false })

type ContentTypeVideo = {
  col?: { from: number, expand: number},
  row?: { from: number, expand: number},
  media: {
    video: {
      longVideo: string | number,
      shortVideo: string | number,
      platform: 'vimeo' | 'youtube' | string
    },
  },
  caption?: string,
  type: 'media' | string,
}

interface IContentVideo {
  data: ContentTypeVideo,
  longModal?: boolean,
  playsinline?: boolean,
  modalActive?: boolean
  insideModal?: boolean
}

export const ContentVideo = memo(({ data, longModal = false, modalActive = false, playsinline = true }:IContentVideo) => {
  const { media } = data
  const { video: { longVideo, platform, shortVideo} } = media

  const [playing, setPlaying] = useState(!longModal)
	const ref = useRef<HTMLDivElement>(null)
	const isInView = useInView(ref, { margin: '0px', once: true })

  const url = `https://www.${platform}.com/${platform === 'youtube' ? 'watch?v=' : ''}${longModal ? longVideo : shortVideo}`

  const { toggle } = useModal(MODALS.VIDEO)

  const onClick = () => {
    if(!longModal) return
    toggle?.()

    // PENDING APPEND MODAL VIDEO TO LAYOUT
    // UPDATE LAYOUT WITH THE VIMEO DATA
  }

  useEffect(() => {
    if(longModal) return
    queueMicrotask(() => setPlaying(isInView))
  }, [isInView, longModal])

  useEffect(() => {
    if(longModal) queueMicrotask(() => setPlaying(modalActive))
  }, [longModal, modalActive])

  return (
    <>
      <VideoContent ref={ref} className='figure--video'>
        <ReactPlayer src={url} playsInline={playsinline} controls={longModal} volume={longModal ? .5 : 0} muted={!longModal} loop={!longModal} playing={playing} />
        {
          longVideo && !longModal &&
          <PlayIcon onClick={onClick}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src='/images/svg/i--hexagon-rounded.svg' alt='Icon Hexagon' width='63' height='69' />
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img className='xs' src='/images/svg/i--hexagon-rounded.svg' alt='Icon Hexagon small'  width='63' height='69'/>
            <span>Play</span>
          </PlayIcon>
        }
      </VideoContent>
    </>
  )
})


const VideoContent = styled.figure`
  height: auto;
  position: relative;
  width: 100%;

  ${mq.greaterThan('desktop-large')} {
    height: 600px;
  }

  img {
    width: 100%;
  }

  > div {
    height: 100%!important;
    width: 100%!important;
  }

  iframe {
    aspect-ratio: 16 / 9;
    height: 100% !important;
    margin-bottom: auto;
    width: 100% !important;

    ${mq.greaterThan('tablet')} {
      transform: translateY(-13%);
    }

    ${mq.greaterThan('desktop-large')} {
      height: 100% !important;
      width: 100% !important;
    }
  }
`


const PlayIcon = styled.button`
  align-items: center;
  border-radius: 100%;
  display: flex;
  height: ${vw(90, 'mobile')};
  justify-content: center;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  transition: height 300ms cubic-bezier(.25, .1, .25, 1), width 300ms cubic-bezier(.25, .1, .25, 1);
  width: ${vw(80, 'mobile')};
  z-index: 2;

  /* ${mq.greaterThan('nexus7')} {
    height: ${vw(90, 'nexus7')};
    width: ${vw(80, 'nexus7')};
  } */

  ${mq.greaterThan('tablet')} {
    height: ${vw(120, 'desktop')};
    width: ${vw(110, 'desktop')};

    &:hover {
      img {
        &:first-child {
          width: ${vw(110, 'desktop')};
        }
        &:nth-child(2) {
          width: ${vw(82, 'desktop')};
        }
      }
    }
  }

  ${mq.greaterThan('desktop')} {
    height: 120px;
    width: 110px;

    &:hover {
      img {
        &:first-child {
          width: 110px;
        }
        &:nth-child(2) {
          width: 82px;
        }
      }
    }
  }

  span {
    color: #ffffff;
    font-size: 10px;
    font-weight: 500;
    left: 50%;
    position: absolute;
    text-transform: uppercase;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 3;
  }

  img {
    left: 50%;
    opacity: .5;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    transition: 300ms width cubic-bezier(.25, .1, .25, 1);
    user-select: none;
    width: ${vw(73, 'mobile')};

    &:first-child {
      transition-delay: 150ms;
    }

    /* ${mq.greaterThan('nexus7')} {
      width: ${vw(73, 'nexus7')};
    } */

    ${mq.greaterThan('tablet')} {
      width: ${vw(91, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      width: 91px;
    }
  }
`
